/** Maritime ops templates (FM:/TO: headers, numbered checklists). */
export function isPlainTextOpsTemplate(text: string): boolean {
  return /\bFM\s*:/i.test(text) && /\bTO\s*:/i.test(text);
}

function reflowSoftWrappedLines(text: string): string {
  const rawLines = text.split('\n');
  const out: string[] = [];
  let buf = '';

  const flush = () => {
    const trimmed = buf.trim();
    if (trimmed) out.push(trimmed);
    buf = '';
  };

  const startsNewBlock = (line: string): boolean => {
    if (/^(FM|TO|RE|CC|BCC)\s*:/i.test(line)) return true;
    if (/^\d{1,3}[).]\s/.test(line)) return true;
    if (/^(Dear\s|Kind regards|Best regards|Yours sincerely)/i.test(line)) return true;
    if (/^_{3,}|-{3,}$/.test(line)) return true;
    return false;
  };

  for (const rawLine of rawLines) {
    const line = rawLine.trim();
    if (!line) {
      flush();
      if (out.length > 0 && out[out.length - 1] !== '') out.push('');
      continue;
    }

    if (!buf) {
      buf = line;
      continue;
    }

    if (startsNewBlock(line)) {
      flush();
      buf = line;
      continue;
    }

    if (buf.endsWith(':')) {
      flush();
      buf = line;
      continue;
    }

    buf += (buf.endsWith('-') ? '' : ' ') + line;
  }

  flush();
  return out.join('\n');
}

export type NormalizePlainTextOptions = {
  /**
   * When true (default), soft-wrapped FM:/TO: ops templates are reflowed into paragraphs.
   * Set false when converting intentional editor Enter/returns (rich compose → text/plain).
   */
  reflowOpsTemplates?: boolean;
};

/** Normalize newlines, trim padding, collapse blank runs; optionally reflow soft-wrapped ops text. */
export function normalizePlainText(
  text: string,
  options?: NormalizePlainTextOptions,
): string {
  const reflowOpsTemplates = options?.reflowOpsTemplates !== false;
  let t = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  t = t.replace(/^[ \t]+|[ \t]+$/gm, '');

  // Compose save/send: keep intentional blank lines from Enter (do not collapse).
  if (!reflowOpsTemplates) {
    t = t.replace(/\n{12,}/g, '\n\n\n\n\n\n\n\n\n\n\n');
    return t.replace(/^\n+/, '').replace(/\n+$/, '');
  }

  t = t.replace(/\n{4,}/g, '\n\n\n');

  const lines = t.split('\n');
  const nonEmpty = lines.filter((l) => l.trim().length > 0).length;
  const empty = lines.length - nonEmpty;

  if (nonEmpty >= 5 && empty > nonEmpty * 0.45) {
    const sectionBreak = /^(Supplies arranged|Shipments\/|Deliveries to|_{3,}|-{3,})/i;
    const out: string[] = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) {
        const nextContent = lines.slice(i + 1).find((l) => l.trim())?.trim() ?? '';
        if (sectionBreak.test(nextContent) && out.length > 0 && out[out.length - 1] !== '') {
          out.push('');
        }
        continue;
      }
      out.push(line.trim());
    }
    t = out.join('\n');
  } else {
    t = t.replace(/\n{3,}/g, '\n\n');
  }

  if (isPlainTextOpsTemplate(t)) {
    t = reflowSoftWrappedLines(t);
  }

  return t;
}
